import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { SessionProvider } from '../features/auth/session';
import type { Api } from '../lib/api';
import { useKeyboardInset } from '../lib/use-keyboard-inset';
import { AppTooltipProvider } from '../ui/Tooltip';
import { ToastHost } from '../ui/Toast';

const ApiContext = createContext<Api | null>(null);

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi() outside <Providers>');
  return api;
}

export function Providers({ api, queryClient, children }: { api: Api; queryClient: QueryClient; children: ReactNode }) {
  // Publishes --kb-inset while a text field is focused, so sheets, panels and the palette stay above the keyboard.
  useKeyboardInset();
  return (
    <ApiContext.Provider value={api}>
      <QueryClientProvider client={queryClient}>
        <AppTooltipProvider>
          <SessionProvider>{children}</SessionProvider>
        </AppTooltipProvider>
        {/* The one toast viewport, a sibling of everything else so it never remounts and drops a queued toast. */}
        <ToastHost />
      </QueryClientProvider>
    </ApiContext.Provider>
  );
}
